import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useAccessToken } from '@hooks/useAccessToken';
import { TRANSFER_RECOMMENDATIONS_QUERY_KEY } from '@hooks/useTransferRecommendations';
import {
  appendPlannerEvents,
  optimisePlannerLineup,
  setPlannerBank,
  type ActivateChipEvent,
  type ChipType,
  type DeactivateChipEvent,
  type PlannerEvent,
  type PlannerStateResponse,
  type SetCaptainEvent,
  type SwapEvent,
  type TransferEvent
} from '@api-service/planner';

const PLANNER_STATE_QUERY_KEY = ['plannerState'] as const;

/**
 * Returns a copy of the cached planner state with the captain/vice armbands
 * reassigned for the given gameweek. Used to optimistically move the armband
 * the instant a user picks a (vice-)captain, rather than waiting for the
 * SET_CAPTAIN round-trip and refetch to land.
 */
const applyCaptaincy = (
  current: PlannerStateResponse | null | undefined,
  gameweek: number,
  captainId: string,
  viceCaptainId: string
): PlannerStateResponse | null => {
  if (!current) return current ?? null;
  const reassign = <T extends { id: string }>(player: T): T => ({
    ...player,
    isCaptain: player.id === captainId,
    isViceCaptain: player.id === viceCaptainId
  });
  return {
    ...current,
    data: {
      ...current.data,
      gameweeks: current.data.gameweeks.map((gw) =>
        gw.gameweek === gameweek
          ? {
              ...gw,
              starting: gw.starting.map(reassign),
              bench: gw.bench.map(reassign)
            }
          : gw
      )
    }
  };
};

type SwapVariables = {
  gameweek: number;
  slots: [number, number];
  /**
   * Optional armband reassignment to batch with the swap. When a substitution
   * benches the (vice-)captain, FPL requires the captain to stay in the XI, so
   * we hand the armband to the incoming player in the same POST. Mirrors
   * scoreline-app's swapPlayers, which sends [SWAP, SET_CAPTAIN] together.
   */
  captaincy?: { captainId: string; viceCaptainId: string };
};

type SetCaptainVariables = {
  gameweek: number;
  captainId: string;
  viceCaptainId: string;
};

type ActivateChipVariables = {
  gameweek: number;
  chip: ChipType;
};

/**
 * `gameweek` is the week the chip is currently scheduled for, not a week to
 * clear: the backend only unschedules the chip if it still finds it there.
 */
type DeactivateChipVariables = {
  gameweek: number;
  chip: ChipType;
};

/** A transfer as the commit mutation takes it; it stamps the event `type`. */
export type TransferVariables = {
  gameweek: number;
  incomingPlayerId: string;
  outgoingPlayerId: string;
};

type CommitTransfersVariables = {
  transfers: TransferVariables[];
};

type OptimiseVariables = {
  gameweek: number;
};

type SetBankVariables = {
  gameweek: number;
  bank: number;
};

const toSwapEvent = ({ gameweek, slots }: SwapVariables): SwapEvent => ({
  type: 'SWAP',
  gameweek,
  slots
});

const toSetCaptainEvent = ({
  gameweek,
  captainId,
  viceCaptainId
}: SetCaptainVariables): SetCaptainEvent => ({
  type: 'SET_CAPTAIN',
  gameweek,
  captainId,
  viceCaptainId
});

const toActivateChipEvent = ({
  gameweek,
  chip
}: ActivateChipVariables): ActivateChipEvent => ({
  type: 'ACTIVATE_CHIP',
  gameweek,
  chip
});

const toDeactivateChipEvent = ({
  gameweek,
  chip
}: DeactivateChipVariables): DeactivateChipEvent => ({
  type: 'DEACTIVATE_CHIP',
  gameweek,
  chip
});

const toTransferEvent = ({
  gameweek,
  incomingPlayerId,
  outgoingPlayerId
}: TransferVariables): TransferEvent => ({
  type: 'TRANSFER',
  gameweek,
  incomingPlayerId,
  outgoingPlayerId
});

/**
 * Domain hook exposing every squad mutation backed by the ffh-backend planner
 * events endpoint plus the optimise endpoint. Each mutation refetches the
 * planner state on success; callers read `error` to surface backend violations.
 */
export const usePlannerMutations = () => {
  const { accessToken } = useAccessToken();
  const queryClient = useQueryClient();

  const invalidatePlannerState = () =>
    queryClient.invalidateQueries({ queryKey: PLANNER_STATE_QUERY_KEY });

  const appendEvents = (events: PlannerEvent[]) => {
    if (!accessToken) {
      throw new Error('No access token available');
    }
    return appendPlannerEvents({ accessToken, events });
  };

  const swap = useMutation({
    mutationFn: ({ gameweek, slots, captaincy }: SwapVariables) => {
      const events: PlannerEvent[] = [toSwapEvent({ gameweek, slots })];
      if (captaincy) {
        // Order matters: the SWAP moves the incoming player into the XI first,
        // then SET_CAPTAIN gives them the armband, so the captain is always in
        // the XI when the backend replays the batch.
        events.push(
          toSetCaptainEvent({
            gameweek,
            captainId: captaincy.captainId,
            viceCaptainId: captaincy.viceCaptainId
          })
        );
      }
      return appendEvents(events);
    },
    onSuccess: invalidatePlannerState
  });

  const setCaptain = useMutation({
    mutationFn: (variables: SetCaptainVariables) =>
      appendEvents([toSetCaptainEvent(variables)]),
    onMutate: async ({ gameweek, captainId, viceCaptainId }) => {
      await queryClient.cancelQueries({ queryKey: PLANNER_STATE_QUERY_KEY });
      const previous = queryClient.getQueryData<PlannerStateResponse | null>(
        PLANNER_STATE_QUERY_KEY
      );
      queryClient.setQueryData<PlannerStateResponse | null>(
        PLANNER_STATE_QUERY_KEY,
        (current) => applyCaptaincy(current, gameweek, captainId, viceCaptainId)
      );
      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context && 'previous' in context) {
        queryClient.setQueryData(PLANNER_STATE_QUERY_KEY, context.previous);
      }
    },
    onSettled: invalidatePlannerState
  });

  const activateChip = useMutation({
    mutationFn: (variables: ActivateChipVariables) =>
      appendEvents([toActivateChipEvent(variables)]),
    onSuccess: invalidatePlannerState
  });

  const deactivateChip = useMutation({
    mutationFn: (variables: DeactivateChipVariables) =>
      appendEvents([toDeactivateChipEvent(variables)]),
    onSuccess: invalidatePlannerState
  });

  const commitTransfers = useMutation({
    mutationFn: ({ transfers }: CommitTransfersVariables) =>
      appendEvents(transfers.map(toTransferEvent)),
    // Committed transfers change the squad, so cached AI transfer
    // recommendations no longer reflect it. Inactive ones are removed
    // outright; the active one (commit from the recommended transfers page)
    // is only marked stale, because removing a query that is still observed
    // recreates it and fires an expensive solver request that races the
    // planner events and blocks the redirect. The transfers page removes it
    // after navigating away, and refetchOnMount covers any remaining stale
    // entry on the next view.
    onSuccess: () =>
      Promise.all([
        invalidatePlannerState(),
        queryClient.invalidateQueries({
          queryKey: TRANSFER_RECOMMENDATIONS_QUERY_KEY,
          refetchType: 'none'
        }),
        queryClient.removeQueries({
          queryKey: TRANSFER_RECOMMENDATIONS_QUERY_KEY,
          type: 'inactive'
        })
      ])
  });

  const optimiseLineup = useMutation({
    mutationFn: ({ gameweek }: OptimiseVariables) => {
      if (!accessToken) {
        throw new Error('No access token available');
      }
      return optimisePlannerLineup({ accessToken, gameweek });
    },
    onSuccess: invalidatePlannerState
  });

  const setBank = useMutation({
    mutationFn: ({ gameweek, bank }: SetBankVariables) => {
      if (!accessToken) {
        throw new Error('No access token available');
      }
      return setPlannerBank({ accessToken, gameweek, bank });
    },
    // A bank override changes the transfer budget, so cached AI transfer
    // recommendations no longer reflect it. Same treatment as commitTransfers:
    // drop inactive entries, only mark the active one stale.
    onSuccess: () =>
      Promise.all([
        invalidatePlannerState(),
        queryClient.invalidateQueries({
          queryKey: TRANSFER_RECOMMENDATIONS_QUERY_KEY,
          refetchType: 'none'
        }),
        queryClient.removeQueries({
          queryKey: TRANSFER_RECOMMENDATIONS_QUERY_KEY,
          type: 'inactive'
        })
      ])
  });

  return {
    swap,
    setCaptain,
    activateChip,
    deactivateChip,
    commitTransfers,
    optimiseLineup,
    setBank
  };
};
